import React, { useState, useEffect } from 'react';
import { Topic, Category, fetchTopics, fetchCategories } from './api';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';

export function TopicManagement() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    categoryId: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [topicsData, categoriesData] = await Promise.all([
        fetchTopics(100),
        fetchCategories(),
      ]);
      setTopics(topicsData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(topic: Topic) {
    setEditingTopic(topic);
    setFormData({
      title: topic.title,
      slug: topic.slug,
      description: topic.description,
      categoryId: topic.categoryId || '',
    });
    setShowModal(true);
  }

  function handleCancel() {
    setEditingTopic(null);
    setShowModal(false);
    setFormData({
      title: '',
      slug: '',
      description: '',
      categoryId: '',
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingTopic) return;

    try {
      const response = await fetch(`/api/forum/topics/${editingTopic.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update topic');
      }

      await loadData();
      handleCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update topic');
    }
  }

  async function handleDelete(topicId: string) {
    if (!confirm('Are you sure you want to delete this topic? All threads and posts will be deleted as well.')) {
      return;
    }

    try {
      const response = await fetch(`/api/forum/topics/${topicId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete topic');
      }

      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete topic');
    }
  }

  async function handleMoveUp(index: number) {
    if (index === 0) return;

    const topic = topics[index];
    const prevTopic = topics[index - 1];

    try {
      // Swap display orders
      const response1 = await fetch(`/api/forum/topics/${topic.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: topic.title,
          slug: topic.slug,
          description: topic.description,
          displayOrder: prevTopic.displayOrder,
          categoryId: topic.categoryId || null,
        }),
      });
      if (!response1.ok) throw new Error('Failed to update first topic');

      const response2 = await fetch(`/api/forum/topics/${prevTopic.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: prevTopic.title,
          slug: prevTopic.slug,
          description: prevTopic.description,
          displayOrder: topic.displayOrder,
          categoryId: prevTopic.categoryId || null,
        }),
      });
      if (!response2.ok) throw new Error('Failed to update second topic');

      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder topics');
    }
  }

  async function handleMoveDown(index: number) {
    if (index === topics.length - 1) return;

    const topic = topics[index];
    const nextTopic = topics[index + 1];

    try {
      // Swap display orders
      const response1 = await fetch(`/api/forum/topics/${topic.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: topic.title,
          slug: topic.slug,
          description: topic.description,
          displayOrder: nextTopic.displayOrder,
          categoryId: topic.categoryId || null,
        }),
      });
      if (!response1.ok) throw new Error('Failed to update first topic');

      const response2 = await fetch(`/api/forum/topics/${nextTopic.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: nextTopic.title,
          slug: nextTopic.slug,
          description: nextTopic.description,
          displayOrder: topic.displayOrder,
          categoryId: nextTopic.categoryId || null,
        }),
      });
      if (!response2.ok) throw new Error('Failed to update second topic');

      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder topics');
    }
  }

  function getCategoryName(categoryId?: string) {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : 'Unknown';
  }

  // Group topics by category
  const groupedTopics = React.useMemo(() => {
    const groups: { [key: string]: Topic[] } = {};

    // Add all categories (even empty ones)
    categories.forEach((cat) => {
      groups[cat.id] = [];
    });

    // Add uncategorized group
    groups['uncategorized'] = [];

    // Distribute topics into groups
    topics.forEach((topic) => {
      const key = topic.categoryId || 'uncategorized';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(topic);
    });

    return groups;
  }, [topics, categories]);

  if (loading) {
    return <div className="p-4">Loading topics...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold dark:text-white">Topic Management</h1>
        <Button variant="secondary" onClick={() => navigate('/forum')}>
          Back to Forum
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded dark:bg-red-900 dark:border-red-600 dark:text-red-200">
          {error}
        </div>
      )}

      <Modal
        isOpen={showModal && !!editingTopic}
        onClose={handleCancel}
        title="Edit Topic"
        size="lg"
      >
        {editingTopic && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Slug *
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
                pattern="[a-z0-9-]+"
                title="Only lowercase letters, numbers, and hyphens"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Category
              </label>
              <select
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">-- No Category --</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">Update</Button>
            </div>
          </form>
        )}
      </Modal>

      <div className="space-y-6">
        {topics.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No topics yet. Create one to get started.</p>
        ) : (
          <>
            {/* Render each category group */}
            {categories.map((category) => {
              const categoryTopics = groupedTopics[category.id] || [];
              if (categoryTopics.length === 0) return null;

              return (
                <div key={category.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  {/* Category header */}
                  <div className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-700 dark:to-blue-800">
                    <h2 className="text-lg font-semibold text-white">{category.name}</h2>
                  </div>

                  {/* Topics table */}
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Sort
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Slug
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {categoryTopics.map((topic, groupIndex) => {
                        const globalIndex = topics.findIndex((t) => t.id === topic.id);
                        return (
                          <tr key={topic.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={() => handleMoveUp(globalIndex)}
                                  disabled={groupIndex === 0}
                                  className={`p-1 rounded text-xs ${
                                    groupIndex === 0
                                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                  }`}
                                  title="Move up within category"
                                >
                                  ▲
                                </button>
                                <button
                                  onClick={() => handleMoveDown(globalIndex)}
                                  disabled={groupIndex === categoryTopics.length - 1}
                                  className={`p-1 rounded text-xs ${
                                    groupIndex === categoryTopics.length - 1
                                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                  }`}
                                  title="Move down within category"
                                >
                                  ▼
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-gray-400 dark:text-gray-500">#{groupIndex + 1}</span>
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {topic.title}
                                  </div>
                                  {topic.description && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                      {topic.description.substring(0, 100)}
                                      {topic.description.length > 100 ? '...' : ''}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                                /{topic.slug}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(topic.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => navigate(`/forum/topics/${topic.slug}`)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleEdit(topic)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(topic.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}

            {/* Render uncategorized topics */}
            {groupedTopics['uncategorized'] && groupedTopics['uncategorized'].length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                {/* Uncategorized header */}
                <div className="px-6 py-3 bg-gradient-to-r from-gray-400 to-gray-500 dark:from-gray-600 dark:to-gray-700">
                  <h2 className="text-lg font-semibold text-white">Uncategorized</h2>
                </div>

                {/* Topics table */}
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Sort
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Slug
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {groupedTopics['uncategorized'].map((topic, groupIndex) => {
                      const globalIndex = topics.findIndex((t) => t.id === topic.id);
                      return (
                        <tr key={topic.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => handleMoveUp(globalIndex)}
                                disabled={groupIndex === 0}
                                className={`p-1 rounded text-xs ${
                                  groupIndex === 0
                                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                                title="Move up"
                              >
                                ▲
                              </button>
                              <button
                                onClick={() => handleMoveDown(globalIndex)}
                                disabled={groupIndex === groupedTopics['uncategorized'].length - 1}
                                className={`p-1 rounded text-xs ${
                                  groupIndex === groupedTopics['uncategorized'].length - 1
                                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                                title="Move down"
                              >
                                ▼
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-gray-400 dark:text-gray-500">#{groupIndex + 1}</span>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {topic.title}
                                </div>
                                {topic.description && (
                                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {topic.description.substring(0, 100)}
                                    {topic.description.length > 100 ? '...' : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                              /{topic.slug}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(topic.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => navigate(`/forum/topics/${topic.slug}`)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEdit(topic)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(topic.id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
